import { useInfiniteQuery } from "@tanstack/react-query";
import { Undo2 } from "lucide-react";
import { api } from "./api";
import { dateLabel, EventItem } from "./components";
import type { WorkEvent } from "../shared/schema";
export function HistoryView({
  timezone,
  onOpen,
  onUndo,
  pending,
}: {
  timezone?: string;
  onOpen: (event: WorkEvent) => void;
  onUndo: (id: string) => void;
  pending: boolean;
}) {
  const query = useInfiniteQuery({
    queryKey: ["history"],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api<{ events: WorkEvent[]; total: number }>(`/history?offset=${pageParam}`),
    getNextPageParam: (last, pages) =>
      pages.length * 100 < last.total ? pages.length * 100 : undefined,
  });
  const events =
    query.data?.pages
      .flatMap((p) => p.events)
      .filter((event, i, all) => all.findIndex((e) => e.id === event.id) === i) ?? [];
  return (
    <div className="history-list">
      {query.error && <p className="inline-alert">{query.error.message}</p>}
      {events.map((event) => (
        <div className="history-row" key={event.id}>
          <EventItem timezone={timezone} event={event} onOpen={() => onOpen(event)} />
          <span className="history-meta">
            {event.actor}
            <br />
            {dateLabel(event.createdAt, timezone)}
          </span>
          {event.previous && (
            <button
              className="icon-button"
              title="Undo this change"
              aria-label={`Undo ${event.title}`}
              disabled={pending}
              onClick={() => onUndo(event.id)}
            >
              <Undo2 size={15} />
            </button>
          )}
        </div>
      ))}
      {query.hasNextPage && (
        <button
          className="button secondary"
          disabled={query.isFetchingNextPage}
          onClick={() => {
            void query.fetchNextPage();
          }}
        >
          Load earlier history
        </button>
      )}
      {query.isLoading && <p className="empty-small">Loading the work history…</p>}
    </div>
  );
}
